import { Button } from "@/components/ui/button";
import { Menu, X, Globe } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { useTranslations } from "@/lib/translations";
import { pushEvent } from "@/lib/analytics";
import { useEngagementTracking } from "@/hooks/use-engagement-tracking";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logoImage from "@assets/cropped-esg-invest-logo-1_1770278231653.png";
import CookieConsent, { OPEN_COOKIE_SETTINGS_EVENT } from "@/components/CookieConsent";

const Layout = ({ children }: { children: React.ReactNode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [location, navigate] = useLocation();
  const [isHome] = useRoute("/");
  const { language, setLanguage, t, isRTL } = useTranslations();

  useEngagementTracking();

  const handleLanguageChange = (next: 'en' | 'ar') => {
    if (next !== language) {
      pushEvent('language_switch', { locale: next, from_locale: language });
    }
    setLanguage(next);
  };

  const handleSigninClick = (location: string) => {
    pushEvent('cta_signin', { location });
  };

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest('a') as HTMLAnchorElement | null;
      if (!anchor) return;
      const rawHref = anchor.getAttribute('href');
      if (!rawHref) return;

      let href = rawHref;
      let linkDomain = '';
      let isOutbound = false;

      if (rawHref.toLowerCase().startsWith('mailto:')) {
        isOutbound = true;
        linkDomain = 'mailto';
        href = rawHref;
      } else {
        try {
          const url = new URL(rawHref, window.location.href);
          if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
          if (url.host && url.host !== window.location.host) {
            isOutbound = true;
            linkDomain = url.host;
            href = url.href;
          }
        } catch {
          return;
        }
      }

      if (!isOutbound) return;

      const linkText = (anchor.textContent || '').trim().slice(0, 200);
      pushEvent('outbound_click', {
        href,
        link_domain: linkDomain,
        link_text: linkText,
      });
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, []);

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.subscriptions'), href: '/#subscriptions' },
    { name: t('nav.companies'), href: '/companies' },
    { name: t('nav.contact'), href: '/contact' },
  ];

  const isActive = (href: string) => {
    if (href === '/#subscriptions') return location === '/subscriptions';
    return location === href;
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href === '/#subscriptions') {
      e.preventDefault();
      if (isHome) {
        document.getElementById('subscriptions')?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => {
          document.getElementById('subscriptions')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
      <nav className="bg-white/95 dark:bg-background/95 backdrop-blur-md border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex justify-between items-center h-20 gap-4">
            <Link href="/" className="flex items-center" data-testid="link-logo">
              <img src={logoImage} alt="ESG Invest" className="h-10" />
            </Link>

            <div className={`hidden md:flex items-center ${isRTL ? 'space-x-reverse space-x-8' : 'space-x-8'}`}>
              {navigation.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => {
                    handleNavClick(e, item.href);
                    if (item.href !== '/#subscriptions') {
                      e.preventDefault();
                      navigate(item.href);
                    }
                  }}
                  className={`text-sm font-medium transition-colors cursor-pointer ${
                    isActive(item.href)
                      ? 'text-se-green'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`link-nav-${item.href.replace('/', '').replace('#', '') || 'home'}`}
                >
                  {item.name}
                </a>
              ))}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="button-language">
                    <Globe className="h-4 w-4" />
                    {language === 'en' ? 'English' : 'العربية'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={() => handleLanguageChange('en')}
                    className={language === 'en' ? 'bg-muted' : ''}
                    data-testid="menu-item-english"
                  >
                    English
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleLanguageChange('ar')}
                    className={language === 'ar' ? 'bg-muted' : ''}
                    data-testid="menu-item-arabic"
                  >
                    العربية (Arabic)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="accent" size="sm" asChild data-testid="button-signin">
                <a href="https://platform.esginvest.com/login" onClick={() => handleSigninClick('header')}>{t('nav.signin')}</a>
              </Button>
            </div>

            <div className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                data-testid="button-mobile-menu"
              >
                {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>

          {isMenuOpen && (
            <div className="md:hidden border-t border-border bg-white/95 dark:bg-background/95 backdrop-blur-md">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navigation.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className={`block px-3 py-2 text-base font-medium transition-colors rounded-md cursor-pointer ${
                      isActive(item.href)
                        ? 'text-se-green bg-se-green-lighter dark:bg-se-green/10'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    onClick={(e) => {
                      setIsMenuOpen(false);
                      handleNavClick(e, item.href);
                      if (item.href !== '/#subscriptions') {
                        e.preventDefault();
                        navigate(item.href);
                      }
                    }}
                    data-testid={`link-mobile-${item.href.replace('/', '').replace('#', '') || 'home'}`}
                  >
                    {item.name}
                  </a>
                ))}
                <div className="px-3 py-2 space-y-2">
                  <div className="flex gap-2 mb-2">
                    <Button 
                      variant={language === 'en' ? 'default' : 'outline'} 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleLanguageChange('en')}
                      data-testid="button-english-mobile"
                    >
                      English
                    </Button>
                    <Button 
                      variant={language === 'ar' ? 'default' : 'outline'} 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleLanguageChange('ar')}
                      data-testid="button-arabic-mobile"
                    >
                      العربية
                    </Button>
                  </div>
                  <Button variant="accent" size="sm" className="w-full" asChild data-testid="button-signin-mobile">
                    <a href="https://platform.esginvest.com/login" onClick={() => handleSigninClick('mobile_menu')}>{t('nav.signin')}</a>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      <main>{children}</main>

      <footer className="bg-white dark:bg-background border-t border-border">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="mb-4">
                <img src={logoImage} alt="ESG Invest" className="h-10" />
              </div>
              <p className="text-muted-foreground mb-4 max-w-md">
                {t('footer.description')}
              </p>
            </div>
            
            
            <div>
              <h3 className="font-semibold mb-4 text-se-navy dark:text-foreground">{t('footer.company')}</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link href="/about" className="hover:text-se-navy dark:hover:text-foreground transition-colors" data-testid="link-footer-about">About</Link></li>
                <li><Link href="/investor-solutions" className="hover:text-se-navy dark:hover:text-foreground transition-colors" data-testid="link-footer-solutions">Investor Solutions</Link></li>
                <li><Link href="/contact" className="hover:text-se-navy dark:hover:text-foreground transition-colors" data-testid="link-footer-contact">{t('nav.contact')}</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4 text-se-navy dark:text-foreground">{t('footer.resources')}</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link href="/methodology" className="hover:text-se-navy dark:hover:text-foreground transition-colors" data-testid="link-footer-methodology">{t('footer.methodology')}</Link></li>
                <li><Link href="/code-of-conduct" className="hover:text-se-navy dark:hover:text-foreground transition-colors" data-testid="link-footer-coc">{t('footer.codeOfConduct')}</Link></li>
                <li><Link href="/privacy" className="hover:text-se-navy dark:hover:text-foreground transition-colors" data-testid="link-footer-privacy">{t('footer.privacy')}</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-border mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-muted-foreground">
            <p>{t('footer.copyright')}</p>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT))}
              className="text-sm hover:text-se-navy dark:hover:text-foreground transition-colors underline-offset-4 hover:underline"
              data-testid="link-footer-cookie-settings"
            >
              {t('footer.cookieSettings')}
            </button>
          </div>
        </div>
      </footer>

      <CookieConsent />
    </div>
  );
};

export default Layout;
